import { useState } from "react";
import { useTheme } from "@/context/ThemeContext";
import { toast } from "sonner";
import { Bookmark, Trash2, Plus, Check } from "lucide-react";

/**
 * Preset picker — save your favorite combo (theme + corners + texture) as a
 * named preset and one-click switch between them. Persisted in localStorage
 * under `peak:presets`.
 */
export default function PresetPicker() {
  const {
    themeId, corners, texture, themes,
    presets, savePreset, applyPreset, deletePreset,
  } = useTheme();
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState("");

  const currentThemeName = themes.find((t) => t.id === themeId)?.name || themeId;

  const handleSave = () => {
    const trimmed = name.trim();
    if (!trimmed) {
      toast.error("Give your preset a name");
      return;
    }
    const p = savePreset(trimmed);
    if (p) {
      toast.success(`Saved "${p.name}"`);
      setName("");
      setCreating(false);
    }
  };

  const handleApply = async (p) => {
    await applyPreset(p.id);
    toast.success(`Switched to "${p.name}"`);
  };

  const handleDelete = (e, p) => {
    e.stopPropagation();
    deletePreset(p.id);
    toast(`Removed "${p.name}"`);
  };

  const isActive = (p) =>
    p.theme === themeId && p.corners === corners && p.texture === texture;

  return (
    <div className="space-y-4" data-testid="preset-picker">
      {presets.length === 0 && !creating && (
        <div className="peak-empty">
          <div className="peak-empty__icon"><Bookmark className="w-5 h-5" /></div>
          <div className="text-[var(--peak-ink)] font-heading font-bold normal-case tracking-tight text-sm">
            No presets yet
          </div>
          <div className="mt-0.5">Save your current combo to switch back with one click</div>
        </div>
      )}

      {presets.length > 0 && (
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {presets.map((p) => {
            const active = isActive(p);
            const swatchTheme = themes.find((t) => t.id === p.theme);
            return (
              <button
                key={p.id}
                type="button"
                onClick={() => handleApply(p)}
                data-testid={`preset-option-${p.id}`}
                data-active={active ? "1" : "0"}
                className={`text-left border-2 p-4 transition-all hover:-translate-y-0.5 relative ${
                  active
                    ? "border-[var(--peak-accent)] shadow-[4px_4px_0_0_var(--peak-shadow)] peak-glow"
                    : "border-[var(--peak-ink)] hover:shadow-[4px_4px_0_0_var(--peak-shadow)]"
                } bg-[var(--peak-surface)]`}
              >
                {/* preview swatches from the preset's theme */}
                {swatchTheme && (
                  <div className="flex h-10 border-2 border-[var(--peak-ink)] overflow-hidden mb-3">
                    {swatchTheme.swatches.map((c, i) => (
                      <div key={i} className="flex-1" style={{ backgroundColor: c }} />
                    ))}
                  </div>
                )}
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="font-heading font-black tracking-tight text-base leading-tight text-[var(--peak-ink)] truncate">
                      {p.name}
                    </div>
                    <div className="text-[10px] font-mono uppercase tracking-[0.15em] text-[var(--peak-muted)] mt-1">
                      {swatchTheme?.name || p.theme} · {p.corners} · {p.texture}
                    </div>
                  </div>
                  {active && (
                    <div
                      className="flex-none w-6 h-6 bg-[var(--peak-accent)] text-[var(--peak-accent-contrast)] flex items-center justify-center"
                      data-testid={`preset-active-${p.id}`}
                    >
                      <Check className="w-3.5 h-3.5" strokeWidth={3} />
                    </div>
                  )}
                </div>
                <div
                  role="button"
                  tabIndex={0}
                  onClick={(e) => handleDelete(e, p)}
                  onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && handleDelete(e, p)}
                  data-testid={`preset-delete-${p.id}`}
                  className="absolute top-2 right-2 w-7 h-7 flex items-center justify-center text-[var(--peak-muted)] hover:text-[var(--peak-accent)] hover:bg-[var(--peak-surface-alt)] cursor-pointer"
                  aria-label={`Delete preset ${p.name}`}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </div>
              </button>
            );
          })}
        </div>
      )}

      {creating ? (
        <div className="flex items-center gap-2" data-testid="preset-create-row">
          <input
            autoFocus
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleSave();
              if (e.key === "Escape") { setCreating(false); setName(""); }
            }}
            placeholder={`e.g. Daytime · ${currentThemeName}`}
            data-testid="preset-name-input"
            className="brutalist-input flex-1"
          />
          <button
            type="button"
            onClick={handleSave}
            data-testid="preset-save-btn"
            className="px-4 py-2 bg-[var(--peak-accent)] text-[var(--peak-accent-contrast)] font-heading font-bold uppercase text-xs tracking-wider hover:bg-[var(--peak-ink)] hover:text-[var(--peak-bg)] transition-colors"
          >
            Save
          </button>
          <button
            type="button"
            onClick={() => { setCreating(false); setName(""); }}
            data-testid="preset-cancel-btn"
            className="px-3 py-2 text-[var(--peak-muted)] hover:text-[var(--peak-ink)] font-mono text-xs uppercase tracking-wider"
          >
            Cancel
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setCreating(true)}
          data-testid="preset-new-btn"
          className="inline-flex items-center gap-2 px-4 py-2 border-2 border-[var(--peak-ink)] font-heading font-bold uppercase text-xs tracking-wider text-[var(--peak-ink)] hover:bg-[var(--peak-ink)] hover:text-[var(--peak-bg)] transition-colors"
        >
          <Plus className="w-4 h-4" />
          Save current combo as preset
        </button>
      )}
    </div>
  );
}
